import { doc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';

// Document number sequences start here at the beginning of each year (and reset
// to this value on year rollover), instead of at 1.
export const SEQUENCE_START = 6000;

// Reserve the next document number for the given docType ('quote' or 'invoice').
//
// Quotes and invoices use separate counter documents in the `counters`
// collection. The read-increment-write runs inside a Firestore transaction so
// two clients creating documents at the same time can never receive the same
// number (the transaction retries on contention).
//
// Returns a formatted string like "26-006001".
export async function generateDocNumber(docType) {
  const year = new Date().getFullYear().toString().slice(-2);
  const counterRef = doc(db, 'counters', docType === 'quote' ? 'quoteCounter' : 'invoiceCounter');

  const next = await runTransaction(db, async (transaction) => {
    const counterSnap = await transaction.get(counterRef);
    let value = SEQUENCE_START;
    if (counterSnap.exists()) {
      const data = counterSnap.data();
      if (data.year === year) value = (data.count || 0) + 1;
    }
    transaction.set(counterRef, { count: value, year });
    return value;
  });

  return `${year}-${String(next).padStart(6, '0')}`;
}

// Reserve the next SHOP DRAWING number.
//
// Same mechanism as generateDocNumber above — a read-increment-write inside a
// Firestore transaction against its own counter document, so concurrent clients
// can never be handed the same number — but a separate `shopDrawingCounter` doc
// and its own format, so the drawing sequence is independent of quotes and
// invoices.
//
// Shop drawings start each year at 1 (not SEQUENCE_START) and use a 4-digit
// counter, giving "SD-26-0001". The counter resets on year rollover exactly as
// the document counters do.
export async function generateShopDrawingNumber() {
  const year = new Date().getFullYear().toString().slice(-2);
  const counterRef = doc(db, 'counters', 'shopDrawingCounter');

  const next = await runTransaction(db, async (transaction) => {
    const counterSnap = await transaction.get(counterRef);
    let value = 1;
    if (counterSnap.exists()) {
      const data = counterSnap.data();
      if (data.year === year) value = (data.count || 0) + 1;
    }
    transaction.set(counterRef, { count: value, year });
    return value;
  });

  return `SD-${year}-${String(next).padStart(4, '0')}`;
}
